/**
 * GapDrawer Component
 *
 * Displays detailed gap information and assignment workflow
 */

import { useState, useEffect } from 'react';
import {
  Drawer,
  Badge,
  Button,
  Group,
  Stack,
  Text,
  Select,
  List,
  Divider,
  LoadingOverlay,
  Alert,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import '@mantine/dates/styles.css';
import { fiConsoleApi } from '../services/fiConsoleApi';
import type { ComplianceGap } from '../types/fiConsole';

interface GapDrawerProps {
  opened: boolean;
  onClose: () => void;
  gapId: string | null;
  onUpdate?: (gap: ComplianceGap) => void;
}

// Mock assignee list (in production, this would come from an API)
const ASSIGNEES = [
  { value: 'cfo@bank.ca', label: 'CFO - Chief Financial Officer' },
  { value: 'cro@bank.ca', label: 'CRO - Chief Risk Officer' },
  { value: 'compliance@bank.ca', label: 'Compliance Team Lead' },
  { value: 'data@bank.ca', label: 'Data Governance Lead' },
  { value: 'tech@bank.ca', label: 'Technology Lead' },
];

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CLOSED', label: 'Closed' },
];

const SEVERITY_COLORS = {
  CRITICAL: 'red',
  HIGH: 'orange',
  MEDIUM: 'yellow',
  LOW: 'blue',
} as const;

const STATUS_COLORS = {
  OPEN: 'red',
  IN_PROGRESS: 'blue',
  RESOLVED: 'green',
  CLOSED: 'gray',
} as const;

export default function GapDrawer({ opened, onClose, gapId, onUpdate }: GapDrawerProps) {
  const [gap, setGap] = useState<ComplianceGap | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  // Fetch gap details when drawer opens
  useEffect(() => {
    if (opened && gapId) {
      fetchGapDetails();
    } else {
      // Reset state when drawer closes
      setGap(null);
      setError(null);
      setSuccessMessage(null);
    }
  }, [opened, gapId]);

  // Initialize form when gap is loaded
  useEffect(() => {
    if (gap) {
      setAssignedTo(gap.assigned_to || null);
      setDueDate(gap.due_date ? new Date(gap.due_date) : null);
      setStatus(gap.status || null);
    }
  }, [gap]);

  const fetchGapDetails = async () => {
    if (!gapId) return;

    setLoading(true);
    setError(null);
    try {
      // Fetch all gaps and find the one we need
      // Note: In production, you'd have a dedicated getGap(id) endpoint
      const gaps = await fiConsoleApi.getGaps();
      const gapData = gaps.find(g => g.id === gapId);
      if (gapData) {
        setGap(gapData);
      } else {
        setError('Gap not found.');
      }
    } catch (err) {
      console.error('Error fetching gap details:', err);
      setError('Failed to load gap details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!gap) return;

    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updates: {
        status?: string;
        assigned_to?: string;
        due_date?: string;
        resolution_notes?: string;
      } = {};

      if (assignedTo !== gap.assigned_to) {
        updates.assigned_to = assignedTo || undefined;
      }
      if (dueDate && dueDate.toISOString() !== gap.due_date) {
        updates.due_date = dueDate.toISOString();
      }
      if (status && status !== gap.status) {
        updates.status = status;
      }

      if (Object.keys(updates).length === 0) {
        setSuccessMessage('No changes to save.');
        return;
      }

      const updatedGap = await fiConsoleApi.updateGap(gap.id, updates);
      setGap(updatedGap);
      setSuccessMessage('Gap updated successfully!');

      // Optimistic UI update - call parent callback
      if (onUpdate) {
        onUpdate(updatedGap);
      }

      // Auto-close success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Error updating gap:', err);
      setError('Failed to update gap. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const hasChanges = () => {
    if (!gap) return false;
    return (
      assignedTo !== gap.assigned_to ||
      (dueDate && dueDate.toISOString() !== gap.due_date) ||
      (status && status !== gap.status)
    );
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={gap ? 'Gap Details' : 'Loading...'}
      padding="xl"
    >
      <LoadingOverlay visible={loading} />

      {error && (
        <Alert color="red" title="Error" mb="md" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert color="green" title="Success" mb="md" withCloseButton onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {gap && (
        <Stack gap="lg">
          {/* Header with severity and status badges */}
          <Group justify="space-between">
            <Badge color={SEVERITY_COLORS[gap.severity]} size="lg">
              {gap.severity}
            </Badge>
            <Badge color={STATUS_COLORS[gap.status]} size="lg">
              {gap.status.replace('_', ' ')}
            </Badge>
          </Group>

          {/* Title */}
          <div>
            <Text size="xl" fw={700} mb="xs">
              {gap.title}
            </Text>
            <Text size="sm" c="dimmed">
              {gap.guideline_id}
            </Text>
          </div>

          <Divider />

          {/* Description */}
          <div>
            <Text fw={600} mb="xs">Description</Text>
            <Text size="sm">{gap.description}</Text>
          </div>

          {/* Affected Entities */}
          {gap.affected_entities && gap.affected_entities.length > 0 && (
            <div>
              <Text fw={600} mb="xs">Affected Entities</Text>
              <List size="sm" spacing="xs">
                {gap.affected_entities.map((entity, idx) => (
                  <List.Item key={idx}>{entity}</List.Item>
                ))}
              </List>
            </div>
          )}

          {/* Remediation Steps */}
          {gap.remediation_steps && gap.remediation_steps.length > 0 && (
            <div>
              <Text fw={600} mb="xs">Remediation Steps</Text>
              <List type="ordered" size="sm" spacing="xs">
                {gap.remediation_steps.map((step, idx) => (
                  <List.Item key={idx}>{step}</List.Item>
                ))}
              </List>
            </div>
          )}

          <Divider />

          {/* Assignment Form */}
          <div>
            <Text fw={600} mb="md" size="lg">Gap Assignment</Text>
            <Stack gap="md">
              <Select
                label="Assigned To"
                placeholder="Select assignee"
                data={ASSIGNEES}
                value={assignedTo}
                onChange={setAssignedTo}
                clearable
                searchable
              />

              <DatePickerInput
                label="Due Date"
                placeholder="Select due date"
                value={dueDate}
                onChange={setDueDate}
                minDate={new Date()}
                clearable
              />

              <Select
                label="Status"
                placeholder="Select status"
                data={STATUS_OPTIONS}
                value={status}
                onChange={setStatus}
              />
            </Stack>
          </div>

          {/* Metadata */}
          <div>
            <Text size="xs" c="dimmed">
              Created: {new Date(gap.created_at).toLocaleString()}
            </Text>
            <Text size="xs" c="dimmed">
              Last Updated: {new Date(gap.updated_at).toLocaleString()}
            </Text>
          </div>

          {/* Action Buttons */}
          <Group justify="flex-end" mt="xl">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={submitting}
              disabled={!hasChanges()}
            >
              Save Changes
            </Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}
